/**
 * @file look-limits.ts
 * @description Пределы чисел оформления секции v3 (phase19.16, партия SB120
 * «свобода выбора», этап 1 «секция-герой»; site-builder.md SB120, SB100;
 * проект данных docs/phases/phase19.16-materials/db-design-sb120-stage1.md §1,
 * проект UI uiux-sb120-stage1.md §6 «Пределы схемы»).
 *
 * Один источник для схемы (schema.ts), чтения с починкой (read.ts), подъёма
 * v2 → v3 (upgrade-v3.ts), предупреждений (look-warnings.ts) и ползунков
 * кабинета (`NumberSliderField` — min/max/шаг). Пределы — то, что платформа
 * держит жёстко (SB120: «числом в разумных пределах»); вкус владельца —
 * предупреждения, а не запреты.
 *
 * Единицы: px — пиксели ПК (телефон считается долей, `phoneFactor`);
 * % — проценты; доли колонок — двенадцатые.
 *
 * SB120 этап 3а (db-design-sb120-stage3 §2): пределы элементов — отступ
 * элемента, высота и рамка кнопки, пропорция плиток, плотность плашки
 * надписи на фото; пороги предупреждений элементов (element-warnings.ts).
 *
 * SB120 этап 3б (db-design-sb120-stage3 §1 «В 3б остаётся чисто
 * добавочным», uiux-sb120-stage3 §3, §7): «в ряд» у сеток, затемнение
 * плиток, смена объявлений, разделитель вопросов, линии таблицы — и пороги
 * «7–8 в ряд», «смена быстрее 5 с». Расширенные пределы без версии секции:
 * нет ключа — как было; старый код после отката заменит новое значение
 * умолчанием (`repairBySchema`, §9).
 */

/** Пределы оформления секции. */
export const SECTION_LOOK_LIMITS = {
  /** Отступы снаружи и внутри сверху/снизу, наезд, выход фото за край, px. */
  space: { min: 0, max: 200 },
  /** Внутри слева/справа у секции и внутренние отступы колонки, px. */
  side: { min: 0, max: 120 },
  /** Доля отступов на телефоне от ПК: секция и тема (`spacing.phoneFactor`). */
  phoneFactor: { min: 0, max: 1, default: 0.6 },
  /** Ширина блока, px; «Во всю» — отдельное значение `"full"`. Нет ключа — «по сетке». */
  container: { min: 320, max: 1920, grid: 1280, narrow: 768 },
  /** Высота «Числом», px. */
  heightPx: { min: 0, max: 1600 },
  /** Высота «Экран», % окна без шапки. */
  heightScreen: { min: 0, max: 100 },
  /** Пропорции кадра (ширина / высота) — у высоты «По фото» и у элемента «Фото». */
  aspect: { min: 0.2, max: 6 },
  /** Скругление подложки, px. */
  cardRadius: { min: 0, max: 64 },
  /** Тень подложки, %. */
  cardShadow: { min: 0, max: 100 },
  /** Ширина растворения края фото, % (section.css: 45 %). */
  fadeWidth: { min: 5, max: 100, default: 45 },
  /** Масштаб кадра фото фона. */
  zoom: { min: 1, max: 4 },
  /** Место рисунка, % от левого верхнего угла секции или колонки. */
  decorPosition: { min: 0, max: 100 },
  /** Размер рисунка (ширина), px. */
  decorSize: { min: 16, max: 640 },
  /** Доли колонок: целые двенадцатые, сумма 12. */
  shares: { min: 1, max: 12, total: 12 },
  /** Расстояния между колонками и элементами, px. */
  gap: { min: 0, max: 200 },
  /** Толщина линии шапки раздела, px (0 — без линии). */
  lineWidth: { min: 0, max: 4 },
  /** Высота фото «Числом» и «По колонке: не меньше», px; умолчание «По колонке» — 256 (min-h-64). */
  imageHeight: { min: 0, max: 1600, columnDefault: 256 },
  // ── SB120 этап 3а: элементы (db-design-sb120-stage3 §2, uiux-sb120-stage3 §7) ──
  /** Отступ элемента сверху/снизу, px (ПК и «своё» телефона). */
  elementSpace: { min: 0, max: 200 },
  /** Высота кнопки, px — это `min-height`, текст растит кнопку; нет ключа — 48. */
  buttonHeight: { min: 40, max: 72, default: 48 },
  /** Пропорция плитки (ширина / высота) у плиток и категорий; ступени 1,5 / 1 / 0,75. */
  tileAspect: { min: 0.5, max: 2, default: 1 },
  /** Плотность плашки надписи на фото, %; нет ключа — 60 (прежняя `bg-black/60`). */
  overlayPlate: { min: 0, max: 100, default: 60 },
  /** Толщина рамки кнопки, px. */
  buttonBorder: { min: 0, max: 4 },
  // ── SB120 этап 3б: сетки и оформление элементов (добавочно, без версии) ──
  /** «В ряд на ПК» у галереи, товаров, объявлений-карточек, статей, цифр, похожих товаров (целые). */
  perRow: { min: 1, max: 6 },
  /** «В ряд на ПК» у плиток и категорий (целые). */
  tileCols: { min: 1, max: 8 },
  /** «В ряд» у отзывов (целые); нет ключа — 3 (лентой по 32 %, сеткой 3 на ПК). */
  reviewsPerRow: { min: 1, max: 4, default: 3 },
  /** Затемнение фото плиток «Текст поверх фото», %; нет ключа — по набору (`tileShadeDefault`). */
  tileShade: { min: 0, max: 80 },
  /** Смена объявлений в полосе, секунд (целые); умолчание нового элемента — 6. */
  intervalSec: { min: 2, max: 30, default: 6 },
  /** Толщина разделителя вопросов, px (целые); нет ключа — 1 px цветом линии. */
  faqDivider: { min: 0, max: 4, default: 1 },
  /** Толщина линий таблицы, px (целые); нет ключа — 1 px между строками, 2 px под шапкой. */
  tableLines: { min: 0, max: 4, default: 1, head: 2 },
} as const;

/**
 * Кадр телефона для проверок и подсказок (uiux-sb120-stage1 §6): 360×640,
 * шапка сайта на телефоне 56 px (globals.css `--site-header-h`). Числа ПК —
 * 1280 (ширина сетки) — для подсказок «около N px на ПК».
 */
export const SECTION_PREVIEW_FRAME = {
  phone: { width: 360, height: 640, header: 56 },
  desktop: { width: 1280 },
} as const;

/**
 * Пороги предупреждений (не запреты, uiux-sb120-stage1 §6): по бокам на
 * телефоне меньше 12 px — текст прилипнет к краю; ширина блока уже 480 px —
 * короткие строки; колонка уже 200 px на ПК — «по 2–3 слова в строке»
 * (2/12 при сетке 1280 ≈ 185 px); «По фото» на телефоне ниже 240 px — полоса.
 */
export const SECTION_WARNING_LIMITS = {
  phoneSideMin: 12,
  containerMin: 480,
  narrowColumnPx: 200,
  photoBandMin: 240,
  /** SB120 этап 3а (uiux-sb120-stage3 §2.1, §7): кнопка ниже 48 px, мелкий H1, шрифтов больше 3. */
  buttonHeightMin: 48,
  h1DesktopMin: 24,
  h1PhoneMin: 21,
  fontFamiliesMax: 3,
  /**
   * SB120 этап 3б (uiux-sb120-stage3 §3, §7): с 7 в ряд плитка на 1280 ≈ 140 px —
   * подпись не влезет; смена объявлений быстрее 5 с — не успеют дочитать.
   */
  gridColsManyFrom: 7,
  announcementIntervalMin: 5,
} as const;
